import React, { useState, useMemo, useRef, useEffect } from "react";
import { timeTableManagementApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./TimeTables.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const COURSES = [
  "B.A.", "B.Sc.", "B.Com.", "B.B.A.", "B.C.A.", "B.Tech.",
  "M.A.", "M.Sc.", "M.Com.", "M.B.A.", "M.C.A.", "M.Tech.", "Ph.D.",
];

const SEMESTERS = [
  "Sem 1", "Sem 2", "Sem 3", "Sem 4",
  "Sem 5", "Sem 6", "Sem 7", "Sem 8",
];

const ACADEMIC_YEARS = ["2023-24", "2024-25", "2025-26", "2026-27"];

const DAYS      = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_SHORT = ["Mon",    "Tue",     "Wed",       "Thu",      "Fri",    "Sat"];

const TIME_SLOTS = [
  "9:00 - 10:00",  "10:00 - 11:00", "11:00 - 12:00",
  "12:00 - 1:00",  "1:00 - 2:00",   "2:00 - 3:00",
  "3:00 - 4:00",   "4:00 - 5:00",
];

const PERIOD_TYPES = ["Lecture", "Lab", "Tutorial", "Break", "Free"];

const TYPE_COLORS = {
  Lecture:  { color: "#4f46e5", bg: "#eef2ff", light: "#c7d2fe" },
  Lab:      { color: "#059669", bg: "#ecfdf5", light: "#a7f3d0" },
  Tutorial: { color: "#0369a1", bg: "#f0f9ff", light: "#bae6fd" },
  Break:    { color: "#d97706", bg: "#fffbeb", light: "#fde68a" },
  Free:     { color: "#94a3b8", bg: "#f8fafc", light: "#e2e8f0" },
};

const STATUS_META = {
  Active:   { label: "Active",   color: "#22c55e", bg: "#f0fdf4", border: "#bbf7d0" },
  Draft:    { label: "Draft",    color: "#64748b", bg: "#f8fafc", border: "#cbd5e1" },
  Archived: { label: "Archived", color: "#ef4444", bg: "#fef2f2", border: "#fecaca" },
};

const COURSE_ICONS = {
  "B.A.":    "🎭", "M.A.":    "🎭",
  "B.Sc.":   "🔬", "M.Sc.":   "🔬",
  "B.Com.":  "💼", "M.Com.":  "💼",
  "B.B.A.":  "📊", "M.B.A.":  "📊",
  "B.C.A.":  "💻", "M.C.A.":  "💻",
  "B.Tech.": "⚙️", "M.Tech.": "⚙️",
  "Ph.D.":   "🎓",
};

const COURSE_COLORS = [
  ["#7c3aed","#f5f3ff"], ["#0ea5e9","#f0f9ff"], ["#10b981","#ecfdf5"],
  ["#f59e0b","#fffbeb"], ["#ec4899","#fdf2f8"], ["#6366f1","#eef2ff"],
  ["#14b8a6","#f0fdfa"], ["#f97316","#fff7ed"], ["#8b5cf6","#f5f3ff"],
  ["#0891b2","#ecfeff"], ["#d97706","#fffbeb"], ["#059669","#ecfdf5"],
  ["#dc2626","#fef2f2"],
];

const getCourseColor = (c) =>
  COURSE_COLORS[COURSES.indexOf(c) % COURSE_COLORS.length] || COURSE_COLORS[0];

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const genCode = (course, sem) =>
  `TT-${(course || "XX").replace(/[^A-Z]/gi, "").slice(0, 3).toUpperCase()}-S${
    (sem || "").replace(/\D/g, "").slice(-1) || "1"
  }-${new Date().getFullYear()}-${String(Date.now()).slice(-3)}`;

const buildEmptyGrid = () => {
  const g = {};
  DAYS.forEach((d) => {
    g[d] = {};
    TIME_SLOTS.forEach((s) => { g[d][s] = null; });
  });
  return g;
};

const defaultForm = {
  _id: null, Timetable_Code: "", Course: "", Semester: "",
  Academic_Year: "2025-26", Effective_From: "", Effective_To: "",
  grid: buildEmptyGrid(), Status: "Draft",
  Approved_By_HOD: "", Remarks: "",
};

const defaultCell = { Subject_Name: "", Faculty_Name: "", Room_No: "", Slot_Type: "Lecture" };

const TimeTables = () => {
  const [timetables, setTimetables]         = useState([]);
  const [loading, setLoading]               = useState(false);
  const [modalType, setModalType]           = useState("");
  const [formData, setFormData]             = useState(defaultForm);
  const [selectedItem, setSelectedItem]     = useState(null);
  const [confirmId, setConfirmId]           = useState(null);
  const [confirmAction, setConfirmAction]   = useState("");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos]       = useState({ top: 0, left: 0 });
  const [filterCourse, setFilterCourse]     = useState("all");
  const [filterSemester, setFilterSemester] = useState("all");
  const [filterStatus, setFilterStatus]     = useState("all");
  const [searchText, setSearchText]         = useState("");
  const [activeTab, setActiveTab]           = useState("info");
  const [cellModal, setCellModal]           = useState(null);
  const [cellForm, setCellForm]             = useState(defaultCell);
  const dropdownRef                         = useRef(null);

  useEffect(() => {
    fetchTimetables();
  }, []);

  const fetchTimetables = async () => {
    try {
      setLoading(true);
      const response = await apiClient(timeTableManagementApi);
      const result = await response.json();
      if (response.ok) {
        const mappedData = (result.data || []).map(tt => {
          const grid = buildEmptyGrid();
          (tt.Schedule || []).forEach(slot => {
            const timeKey = `${slot.Start_Time} - ${slot.End_Time}`;
            if (grid[slot.Day] && TIME_SLOTS.includes(timeKey)) {
              grid[slot.Day][timeKey] = {
                Subject_Name: slot.Subject_Name,
                Faculty_Name: slot.Faculty_Name,
                Room_No:      slot.Room_No,
                Slot_Type:    slot.Slot_Type
              };
            }
          });
          return { ...tt, grid };
        });
        setTimetables(mappedData);
      }
    } catch (error) {
      console.error("Error fetching timetables:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onOut   = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null); };
    const onClose = () => setDropdownOpenId(null);
    document.addEventListener("mousedown", onOut);
    window.addEventListener("scroll",  onClose, true);
    window.addEventListener("resize",  onClose);
    return () => {
      document.removeEventListener("mousedown", onOut);
      window.removeEventListener("scroll",  onClose, true);
      window.removeEventListener("resize",  onClose);
    };
  }, []);

  const filtered = useMemo(() =>
    timetables.filter((t) => {
      const q = searchText.toLowerCase();
      return (
        ((t.Course || "").toLowerCase().includes(q) ||
         (t.Timetable_Code || "").toLowerCase().includes(q) ||
         (t.Semester || "").toLowerCase().includes(q) ||
         (t.Approved_By_HOD || "").toLowerCase().includes(q)) &&
        (filterCourse   === "all" || t.Course   === filterCourse)   &&
        (filterSemester === "all" || t.Semester === filterSemester) &&
        (filterStatus   === "all" || t.Status   === filterStatus)
      );
    }),
  [timetables, searchText, filterCourse, filterSemester, filterStatus]);

  const activeCount   = timetables.filter((t) => t.Status === "Active").length;
  const draftCount    = timetables.filter((t) => t.Status === "Draft").length;
  const archivedCount = timetables.filter((t) => t.Status === "Archived").length;
  const hasFilters    = filterCourse !== "all" || filterSemester !== "all" || filterStatus !== "all" || !!searchText;
  const currentDDItem = timetables.find((t) => t._id === dropdownOpenId);

  const countPeriods = (grid) => {
    let n = 0;
    DAYS.forEach((d) => TIME_SLOTS.forEach((s) => { if (grid[d]?.[s]) n++; }));
    return n;
  };

  const getSubjects = (grid) => {
    const set = new Set();
    DAYS.forEach((d) => TIME_SLOTS.forEach((s) => {
      const c = grid[d]?.[s];
      if (c?.Subject_Name && c.Slot_Type !== "Break" && c.Slot_Type !== "Free") set.add(c.Subject_Name);
    }));
    return [...set];
  };

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const mW = 210, mH = 230, gap = 8;
    let left = rect.right - mW;
    let top  = rect.bottom + gap;
    if (left < 12) left = 12;
    if (left + mW > window.innerWidth - 12) left = window.innerWidth - mW - 12;
    if (top + mH > window.innerHeight - 12) top = rect.top - mH - gap;
    if (top < 12) top = 12;
    setDropdownPos({ top, left });
    setDropdownOpenId(id);
  };

  const openAddModal  = () => {
    setFormData({ ...defaultForm, Timetable_Code: genCode("", ""), Effective_From: new Date().toISOString().slice(0, 10), grid: buildEmptyGrid() });
    setActiveTab("info");
    setModalType("add");
    setDropdownOpenId(null);
  };
  const openViewModal = (t) => { setSelectedItem(t); setModalType("view"); setDropdownOpenId(null); };
  const openEditModal = (t) => {
    setFormData({ ...t, grid: JSON.parse(JSON.stringify(t.grid)) });
    setActiveTab("info");
    setModalType("edit");
    setDropdownOpenId(null);
  };
  const closeModal  = () => { setModalType(""); setSelectedItem(null); setFormData(defaultForm); setCellModal(null); };
  const openConfirm = (id, action) => { setDropdownOpenId(null); setConfirmId(id); setConfirmAction(action); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => {
      const next = { ...p, [name]: value };
      if (name === "Course")   next.Timetable_Code = genCode(value, p.Semester);
      if (name === "Semester") next.Timetable_Code = genCode(p.Course, value);
      return next;
    });
  };

  const openCellModal = (day, slot, e) => {
    e.stopPropagation();
    const existing = formData.grid[day]?.[slot];
    setCellForm(existing ? { ...existing } : { ...defaultCell });
    setCellModal({ day, slot });
  };

  const saveCellForm = () => {
    if (!cellModal) return;
    const { day, slot } = cellModal;
    setFormData((p) => ({
      ...p,
      grid: {
        ...p.grid,
        [day]: { ...p.grid[day], [slot]: cellForm.Subject_Name ? { ...cellForm } : null },
      },
    }));
    setCellModal(null);
    setCellForm(defaultCell);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const schedule = [];
      Object.keys(formData.grid).forEach(day => {
        Object.keys(formData.grid[day]).forEach(slot => {
          const cell = formData.grid[day][slot];
          if (cell) {
            const [start, end] = slot.split(" - ");
            schedule.push({
              ...cell,
              Day: day,
              Start_Time: start,
              End_Time: end
            });
          }
        });
      });

      const payload = { ...formData, Schedule: schedule };
      delete payload.grid;
      if (modalType === "add") delete payload._id;
      
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? timeTableManagementApi : `${timeTableManagementApi}/${formData._id}`;
      
      const response = await apiClient(url, {
        method,
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        fetchTimetables();
        closeModal();
      }
    } catch (error) {
      console.error("Error saving timetable:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      setLoading(true);
      const tt = timetables.find(t => t._id === id);
      const payload = { ...tt, Status: status };
      delete payload.grid;

      const response = await apiClient(`${timeTableManagementApi}/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        fetchTimetables();
        setConfirmId(null);
      }
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await apiClient(`${timeTableManagementApi}/${id}`, {
        method: "DELETE"
      });
      if (response.ok) {
        fetchTimetables();
        setConfirmId(null);
      }
    } catch (error) {
      console.error("Error deleting timetable:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearAllFilters = () => {
    setFilterCourse("all"); setFilterSemester("all"); setFilterStatus("all"); setSearchText("");
  };

  const ViewGrid = ({ grid }) => (
    <div className="view-grid-scroll">
      <div className="view-grid" style={{ gridTemplateColumns: `100px repeat(${DAYS.length}, 1fr)` }}>
        <div className="vg-corner" />
        {DAYS.map((d) => <div key={d} className="vg-day-head">{d}</div>)}
        {TIME_SLOTS.map((slot) => (
          <React.Fragment key={slot}>
            <div className="vg-time-label">{slot}</div>
            {DAYS.map((day) => {
              const cell = grid[day]?.[slot];
              const tc   = cell ? TYPE_COLORS[cell.Slot_Type] || TYPE_COLORS.Lecture : null;
              return (
                <div key={day} className={`vg-cell ${cell ? "filled" : ""}`}
                  style={tc ? { background: tc.bg, borderColor: `${tc.color}33` } : {}}>
                  {cell && (
                    <>
                      <div className="vgc-bar" style={{ background: tc.color }} />
                      <div className="vgc-inner">
                        <div className="vgc-subject" style={{ color: tc.color }}>{cell.Subject_Name}</div>
                        {cell.Faculty_Name && <div className="vgc-sub">{cell.Faculty_Name}</div>}
                        {cell.Room_No    && <div className="vgc-sub">{cell.Room_No}</div>}
                        <div className="vgc-type" style={{ color: tc.color, background: tc.light }}>
                          {cell.Slot_Type}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  return (
    <div className="tt-page">
      <div className="tt-header">
        <div className="tt-header-left">
          <div className="tt-header-icon">🗓</div>
          <div>
            <div className="tt-breadcrumb">
              <span>Department at a Glance</span>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
              <span className="tt-bc-active">Time Tables</span>
            </div>
            <h1>Time Table Management</h1>
            <p>Select a course and semester to build the weekly schedule — assign subjects, faculty, rooms and period types.</p>
          </div>
        </div>
        <button type="button" className="tt-add-btn" onClick={openAddModal}>
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5"  y1="12" x2="19" y2="12" /></svg>
          Create Timetable
        </button>
      </div>

      <div className="tt-stats">
        {[
          { cls:"total",    icon:"📋", label:"Total",    val:timetables.length, key:"all"      },
          { cls:"active",   icon:"✅", label:"Active",   val:activeCount,       key:"Active"   },
          { cls:"draft",    icon:"✏️", label:"Drafts",   val:draftCount,        key:"Draft"    },
          { cls:"archived", icon:"🗄️", label:"Archived", val:archivedCount,     key:"Archived" },
        ].map((s) => (
          <div key={s.cls} className={`tt-stat-card ${s.cls} ${filterStatus === s.key ? "active-filter" : ""}`} onClick={() => setFilterStatus(s.key)}>
            <div className="stat-icon-box">{s.icon}</div>
            <div className="stat-content"><span>{s.label}</span><strong>{s.val}</strong></div>
          </div>
        ))}
      </div>

      <div className="tt-toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input type="text" placeholder="Search by course, code, semester, faculty…" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
          {searchText && <button type="button" className="clear-search" onClick={() => setSearchText("")}>×</button>}
        </div>
        <div className="toolbar-filters">
          <div className="toolbar-filter-group">
            <label>Course</label>
            <select value={filterCourse} onChange={(e) => { setFilterCourse(e.target.value); setFilterSemester("all"); }} className={`toolbar-select ${filterCourse !== "all" ? "active-select" : ""}`}>
              <option value="all">All Courses</option>
              {COURSES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="toolbar-filter-group">
            <label>Semester</label>
            <select value={filterSemester} onChange={(e) => setFilterSemester(e.target.value)} className={`toolbar-select ${filterSemester !== "all" ? "active-select" : ""}`}>
              <option value="all">All Semesters</option>
              {SEMESTERS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="toolbar-filter-group">
            <label>Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={`toolbar-select ${filterStatus !== "all" ? "active-select" : ""}`}>
              <option value="all">All Statuses</option>
              {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          {hasFilters && <button type="button" className="clear-filters-btn" onClick={clearAllFilters}><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>Clear</button>}
          <span className="result-count"><strong>{filtered.length}</strong> of <strong>{timetables.length}</strong></span>
        </div>
      </div>

      {hasFilters && (
        <div className="active-filter-pills">
          {searchText && <span className="filter-pill">"{searchText}" <button type="button" onClick={() => setSearchText("")}>×</button></span>}
          {filterCourse   !== "all" && <span className="filter-pill">Course: {filterCourse} <button type="button" onClick={() => setFilterCourse("all")}>×</button></span>}
          {filterSemester !== "all" && <span className="filter-pill">{filterSemester} <button type="button" onClick={() => setFilterSemester("all")}>×</button></span>}
          {filterStatus   !== "all" && <span className="filter-pill">Status: {STATUS_META[filterStatus]?.label} <button type="button" onClick={() => setFilterStatus("all")}>×</button></span>}
          <button type="button" className="pill-clear-all" onClick={clearAllFilters}>Clear all</button>
        </div>
      )}

      {loading && <div className="loading-overlay">Loading...</div>}
      {filtered.length > 0 ? (
        <div className="tt-grid-cards">
          {filtered.map((tt) => {
            const sm       = STATUS_META[tt.Status];
            const [ac, ab] = getCourseColor(tt.Course);
            const icon     = COURSE_ICONS[tt.Course] || "📘";
            const subjects = getSubjects(tt.grid);
            const typeCounts = {};
            DAYS.forEach((d) => TIME_SLOTS.forEach((s) => {
              const c = tt.grid[d]?.[s];
              if (c) typeCounts[c.Slot_Type] = (typeCounts[c.Slot_Type] || 0) + 1;
            }));
            return (
              <div key={tt._id} className="tt-card" onClick={() => openViewModal(tt)}>
                <div className="ttc-head">
                  <div className="ttc-icon-wrap" style={{ background: ab }}><span style={{ color: ac, fontSize: 24 }}>{icon}</span></div>
                  <div className="ttc-meta"><span className="ttc-code">{tt.Timetable_Code}</span><span className="ttc-ay">{tt.Academic_Year}</span></div>
                  <div className="ttc-right">
                    <span className="status-badge" style={{ color: sm?.color, background: sm?.bg, border: `1px solid ${sm?.border}` }}>● {sm?.label}</span>
                    <button type="button" className="dots-btn" onClick={(e) => handleDotsClick(e, tt._id)}>⋮</button>
                  </div>
                </div>
                <div className="ttc-course" style={{ color: ac }}>{tt.Course}</div>
                <div className="ttc-sem">{tt.Semester}</div>
                <div className="ttc-type-pills">
                  {Object.entries(typeCounts).map(([type, count]) => {
                    const tc = TYPE_COLORS[type] || TYPE_COLORS.Lecture;
                    return <span key={type} className="type-pill" style={{ color: tc.color, background: tc.bg, border: `1px solid ${tc.light}` }}>{count} {type}</span>;
                  })}
                </div>
                <div className="ttc-mini-grid">
                  <div className="tmg-head">{DAY_SHORT.map((d) => <span key={d}>{d}</span>)}</div>
                  <div className="tmg-body">
                    {TIME_SLOTS.map((slot) => (
                      <div key={slot} className="tmg-row">
                        {DAYS.map((day) => {
                          const cell = tt.grid[day]?.[slot];
                          const tc   = cell ? TYPE_COLORS[cell.Slot_Type] || TYPE_COLORS.Lecture : null;
                          return <div key={day} className="tmg-cell" style={tc ? { background: tc.bg } : {}}>{cell && <div className="tmg-dot" style={{ background: tc.color }} />}</div>;
                        })}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="ttc-subjects">{subjects.slice(0, 3).map((s) => <span key={s} className="subject-tag">{s}</span>)}{subjects.length > 3 && <span className="subject-tag more">+{subjects.length - 3}</span>}</div>
                <div className="ttc-footer">
                  <span><svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>{tt.Approved_By_HOD || "—"}</span>
                  <span><svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8"  y1="2" x2="8"  y2="6" /><line x1="3"  y1="10" x2="21" y2="10" /></svg>{fmtDate(tt.Effective_From)}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="tt-empty">
          <div className="empty-icon">🗓</div><h3>No timetables found</h3><p>{hasFilters ? "Try adjusting your filters." : "Click \"Create Timetable\" to get started."}</p>
          {hasFilters ? <button type="button" className="tt-clear-btn" onClick={clearAllFilters}>Clear Filters</button> : <button type="button" className="tt-add-btn" onClick={openAddModal}><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5"  y1="12" x2="19" y2="12" /></svg>Create Timetable</button>}
        </div>
      )}

      {dropdownOpenId !== null && (
        <div ref={dropdownRef} className="action-dropdown" style={{ position:"fixed", top:`${dropdownPos.top}px`, left:`${dropdownPos.left}px`, zIndex:99999 }}>
          <button type="button" onClick={() => openViewModal(timetables.find((t) => t._id === dropdownOpenId))}><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" /><circle cx="12" cy="12" r="3" /></svg>View Timetable</button>
          <button type="button" onClick={() => openEditModal(timetables.find((t) => t._id === dropdownOpenId))}><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>Edit Timetable</button>
          <div className="dropdown-divider" />
          {currentDDItem?.Status !== "Active" && <button type="button" className="dd-activate" onClick={() => openConfirm(dropdownOpenId, "activate")}><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>Activate</button>}
          {currentDDItem?.Status !== "Draft" && <button type="button" className="dd-draft" onClick={() => openConfirm(dropdownOpenId, "draft")}><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>Move to Draft</button>}
          {currentDDItem?.Status !== "Archived" && <button type="button" className="dd-archive" onClick={() => openConfirm(dropdownOpenId, "archive")}><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" /></svg>Archive</button>}
          <div className="dropdown-divider" />
          <button type="button" className="dd-delete" onClick={() => openConfirm(dropdownOpenId, "delete")}><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg>Delete</button>
        </div>
      )}

      {confirmId !== null && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`confirm-icon ci-${confirmAction}`}>
              {(confirmAction === "delete" || confirmAction === "archive") && <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg>}
              {confirmAction === "activate" && <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>}
              {confirmAction === "draft" && <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>}
            </div>
            <h3>{confirmAction === "delete" ? "Delete Timetable?" : confirmAction === "activate" ? "Activate Timetable?" : confirmAction === "draft" ? "Move to Draft?" : "Archive Timetable?"}</h3>
            <p>{confirmAction === "delete" ? "Permanently deleted. This cannot be undone." : confirmAction === "activate" ? "Timetable will be active and visible to students." : confirmAction === "draft" ? "Timetable will be hidden and moved back to draft." : "Timetable will be archived and no longer active."}</p>
            <div className="confirm-actions">
              <button type="button" className="btn btn-light" onClick={() => setConfirmId(null)}>Cancel</button>
              <button type="button" className={`btn ${confirmAction === "delete" || confirmAction === "archive" ? "btn-danger" : confirmAction === "activate" ? "btn-success" : "btn-secondary"}`} onClick={() => { if (confirmAction === "delete") handleDelete(confirmId); else if (confirmAction === "activate") handleStatusUpdate(confirmId, "Active"); else if (confirmAction === "draft") handleStatusUpdate(confirmId, "Draft"); else if (confirmAction === "archive") handleStatusUpdate(confirmId, "Archived"); }}>{confirmAction === "delete" ? "Yes, Delete" : confirmAction === "activate" ? "Activate" : confirmAction === "draft" ? "Move to Draft" : "Archive"}</button>
            </div>
          </div>
        </div>
      )}

      {(modalType === "add" || modalType === "edit") && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="tt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-header-icon">{formData.Course ? COURSE_ICONS[formData.Course] || "📘" : "🗓"}</div>
                <div><h2>{modalType === "add" ? "Create Timetable" : "Edit Timetable"}</h2><span>{formData.Course ? `${formData.Course}${formData.Semester ? " · " + formData.Semester : ""}` : "Select course & semester to begin"}</span></div>
              </div>
              <button type="button" className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-tabs">
              {[
                { key:"info",      label:"Course & Info",   icon:"🏛", done: !!formData.Course && !!formData.Semester },
                { key:"timetable", label:"Build Timetable", icon:"🗓", done: countPeriods(formData.grid) > 0 },
                { key:"status",    label:"Status",          icon:"⚙️", done: false },
              ].map((tab, idx) => (
                <button key={tab.key} type="button" className={`modal-tab ${activeTab === tab.key ? "active" : ""} ${tab.done && activeTab !== tab.key ? "done" : ""}`} onClick={() => setActiveTab(tab.key)}><span className="tab-step">{idx + 1}</span><span>{tab.icon}</span>{tab.label}{tab.done && <span className="tab-check">✓</span>}</button>
              ))}
            </div>
            <form className="modal-form" onSubmit={handleSave}>
              {activeTab === "info" && (
                <div className="tab-content">
                  <div className="form-group full">
                    <label>Select Course <span className="req">*</span></label>
                    <div className="course-picker-grid">
                      {COURSES.map((c) => {
                        const [ac, ab] = getCourseColor(c);
                        const sel = formData.Course === c;
                        return <button key={c} type="button" className={`cpc-card ${sel ? "selected" : ""}`} style={sel ? { borderColor: ac, background: ab } : {}} onClick={() => handleChange({ target: { name:"Course", value:c } })}><span style={{ color: sel ? ac : "#64748b", fontSize: 20 }}>{COURSE_ICONS[c] || "📘"}</span><span style={{ color: sel ? ac : "#1e293b", fontSize: 12, fontWeight: 700 }}>{c}</span></button>;
                      })}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Semester <span className="req">*</span></label>
                      <div className="sem-picker">{SEMESTERS.map((s) => <button key={s} type="button" className={`sem-btn ${formData.Semester === s ? "selected" : ""}`} onClick={() => handleChange({ target: { name:"Semester", value:s } })}>{s.replace("Semester ", "Sem ")}</button>)}</div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label>Academic Year</label><select name="Academic_Year" value={formData.Academic_Year || ""} onChange={handleChange} className="modal-select">{ACADEMIC_YEARS.map(y => <option key={y} value={y}>{y}</option>)}</select></div>
                    <div className="form-group"><label>Timetable Code</label><input type="text" name="Timetable_Code" value={formData.Timetable_Code || ""} onChange={handleChange} placeholder="Automatic" /></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label>Effective From</label><input type="date" name="Effective_From" value={formData.Effective_From || ""} onChange={handleChange} /></div>
                    <div className="form-group"><label>Effective To (Optional)</label><input type="date" name="Effective_To" value={formData.Effective_To || ""} onChange={handleChange} /></div>
                  </div>
                </div>
              )}
              {activeTab === "timetable" && (
                <div className="tab-content">
                  <div className="builder-scroll">
                    <div className="builder-grid" style={{ gridTemplateColumns: `100px repeat(${DAYS.length}, 1fr)` }}>
                      <div className="bg-corner" />
                      {DAYS.map(d => <div key={d} className="bg-day-head">{d}</div>)}
                      {TIME_SLOTS.map(slot => (
                        <React.Fragment key={slot}>
                          <div className="bg-time">{slot}</div>
                          {DAYS.map(day => {
                            const cell = formData.grid[day][slot];
                            const tc   = cell ? TYPE_COLORS[cell.Slot_Type] || TYPE_COLORS.Lecture : null;
                            return (
                              <div key={day} 
                                className={`bg-cell ${cell ? "has-entry" : "empty"}`} 
                                style={tc ? { background: tc.bg, borderColor: tc.light } : {}} 
                                onClick={(e) => openCellModal(day, slot, e)}>
                                {cell ? (
                                  <>
                                    <div className="bgc-bar" style={{ background: tc.color }} />
                                    <div className="bgc-body">
                                      <div className="bgc-subject" style={{ color: tc.color }}>{cell.Subject_Name}</div>
                                      <div className="bgc-sub">{cell.Faculty_Name}</div>
                                      <div className="bgc-sub">{cell.Room_No}</div>
                                      <div className="bgc-type" style={{ color: tc.color, background: tc.light }}>{cell.Slot_Type}</div>
                                    </div>
                                    <button type="button" className="bgc-clear" onClick={(e) => {
                                      e.stopPropagation();
                                      setFormData(p => ({ ...p, grid: { ...p.grid, [day]: { ...p.grid[day], [slot]: null } } }));
                                    }}>×</button>
                                  </>
                                ) : <div className="bgc-add">+</div>}
                              </div>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {activeTab === "status" && (
                <div className="tab-content">
                  <div className="form-group full">
                    <label>Approved By (HOD Name)</label>
                    <input type="text" name="Approved_By_HOD" value={formData.Approved_By_HOD || ""} onChange={handleChange} placeholder="e.g. Dr. Rajesh Kumar" />
                  </div>
                  
                  <div className="form-group full">
                    <label>Status</label>
                    <div className="status-grid">
                      {Object.entries(STATUS_META).map(([k, v]) => (
                        <label key={k} className={`status-card ${formData.Status === k ? "selected" : ""}`} style={formData.Status === k ? { borderColor: v.color, background: v.bg } : {}}>
                          <input type="radio" name="Status" value={k} checked={formData.Status === k} onChange={handleChange} />
                          <div>
                            <div className="sc-dot" style={{ background: v.color }} />
                            <div>
                              <strong style={formData.Status === k ? { color: v.color } : {}}>{v.label}</strong>
                              <p>
                                {k === "Active" ? "Visible to all students" : 
                                 k === "Draft" ? "Save as draft for now" : 
                                 "Archived version"}
                              </p>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group full">
                    <label>Remarks / Internal Notes</label>
                    <textarea name="Remarks" value={formData.Remarks || ""} onChange={handleChange} rows="4" placeholder="Optional notes about this version..." />
                  </div>
                </div>
              )}
              <div className="modal-footer"><div className="modal-footer-left"><button type="button" className="btn btn-light" onClick={closeModal}>Cancel</button></div><div className="modal-footer-right">{activeTab !== "info" && <button type="button" className="btn btn-secondary" onClick={() => setActiveTab(activeTab === "status" ? "timetable" : "info")}>Previous</button>}{activeTab !== "status" ? <button type="button" className="btn btn-primary" onClick={() => setActiveTab(activeTab === "info" ? "timetable" : "status")}>Next Step</button> : <button type="submit" className="btn btn-success" disabled={loading}>{loading ? "Saving..." : modalType === "add" ? "Create Timetable" : "Save Changes"}</button>}</div></div>
            </form>
          </div>
        </div>
      )}

      {modalType === "view" && selectedItem && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="tt-modal view-mode" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-header-icon">{COURSE_ICONS[selectedItem.Course] || "📘"}</div>
                <div><h2>{selectedItem.Course} — {selectedItem.Semester}</h2><span>Code: {selectedItem.Timetable_Code} · AY: {selectedItem.Academic_Year}</span></div>
              </div>
              <div className="modal-header-actions"><button type="button" className="btn btn-primary btn-sm" onClick={() => openEditModal(selectedItem)}>Edit</button><button type="button" className="modal-close" onClick={closeModal}>×</button></div>
            </div>
            <div className="view-modal-content">
              <div className="view-meta-bar">
                <div className="vmb-item"><label>Status</label><span className={`status-badge ${selectedItem.Status.toLowerCase()}`}>{selectedItem.Status}</span></div>
                <div className="vmb-item"><label>Approved By</label><strong>{selectedItem.Approved_By_HOD || "—"}</strong></div>
                <div className="vmb-item"><label>Effective From</label><strong>{fmtDate(selectedItem.Effective_From)}</strong></div>
                {selectedItem.Remarks && <div className="vmb-item full"><label>Remarks</label><p>{selectedItem.Remarks}</p></div>}
              </div>
              <ViewGrid grid={selectedItem.grid} />
            </div>
          </div>
        </div>
      )}

      {cellModal && (
        <div className="modal-overlay cell-modal-overlay" onClick={() => setCellModal(null)}>
          <div className="cell-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cell-modal-header"><h3>Edit Slot: {cellModal.day}, {cellModal.slot}</h3><button type="button" className="modal-close" onClick={() => setCellModal(null)}>×</button></div>
            <div className="cell-modal-body">
              <div className="form-group"><label>Subject Name</label><input type="text" value={cellForm.Subject_Name || ""} onChange={(e) => setCellForm({...cellForm, Subject_Name: e.target.value})} placeholder="e.g. Operating Systems" /></div>
              <div className="form-group"><label>Faculty Name</label><input type="text" value={cellForm.Faculty_Name || ""} onChange={(e) => setCellForm({...cellForm, Faculty_Name: e.target.value})} placeholder="e.g. Dr. Rajesh" /></div>
              <div className="form-row"><div className="form-group"><label>Room No.</label><input type="text" value={cellForm.Room_No || ""} onChange={(e) => setCellForm({...cellForm, Room_No: e.target.value})} placeholder="e.g. 101" /></div><div className="form-group"><label>Type</label><select value={cellForm.Slot_Type || "Lecture"} onChange={(e) => setCellForm({...cellForm, Slot_Type: e.target.value})} className="modal-select">{PERIOD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></div></div>
            </div>
            <div className="cell-modal-footer"><button type="button" className="btn btn-light" onClick={() => { setFormData(p => ({ ...p, grid: { ...p.grid, [cellModal.day]: { ...p.grid[cellModal.day], [cellModal.slot]: null } } })); setCellModal(null); }}>Clear Slot</button><button type="button" className="btn btn-primary" onClick={saveCellForm}>Apply Changes</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeTables;